import type { VideoItem } from "@/types/video";
import { videos } from "@/mocks/videos";
import { useSimulatedLoading } from "@/hooks/useSimulatedLoading";
import Seo from "@/components/feature/Seo";
import SectionHeading from "@/components/feature/SectionHeading";
import VideoCard from "@/components/feature/VideoCard";
import LoadingGrid from "@/components/feature/LoadingGrid";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryGrid from "@/pages/home/components/CategoryGrid";
import PopularList from "@/pages/home/components/PopularList";
import SiteIntro from "@/pages/home/components/SiteIntro";

export default function Home() {
  const loading = useSimulatedLoading();
  const allVideos: VideoItem[] = videos;

  const featured = allVideos.filter((video) => video.featured).slice(0, 3);
  const latest = [...allVideos]
    .sort((a, b) => b.uploadDate.localeCompare(a.uploadDate))
    .slice(0, 8);
  const popular = [...allVideos].sort((a, b) => b.views - a.views).slice(0, 6);

  return (
    <>
      <Seo
        title="黄瓜视频下载-高清视频内容聚合与资源导航"
        description="黄瓜视频下载提供丰富的高清视频内容聚合，涵盖热门视频资源和多样化分类，方便用户查找并了解黄瓜视频相关信息与资源流向。"
        keywords="黄瓜视频下载,视频资源,高清视频,视频内容,资源导航,影视视频,视频聚合,视频平台"
      />

      <section className="mx-auto w-full max-w-7xl px-4 pt-6 md:px-6 md:pt-8">
        {loading ? (
          <div className="flex h-[320px] w-full animate-pulse items-center justify-center rounded-lg bg-background-200 sm:h-[400px] lg:h-[480px]">
            <span className="flex items-center gap-2 text-sm text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-lg"></i>
              正在加载焦点视频…
            </span>
          </div>
        ) : (
          <HeroCarousel videos={featured} />
        )}
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 pt-12 md:px-6">
        <SectionHeading title="视频分类" subtitle="按类型快速进入对应分类，浏览高清视频资源" moreTo="/search" moreLabel="全部视频" />
        <CategoryGrid />
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 pt-12 md:px-6">
        <SectionHeading title="最新视频" subtitle="按上传时间排序的最新视频条目" moreTo="/search" />
        {loading ? <LoadingGrid count={8} /> : <VideoGrid videos={latest} />}
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 pt-12 md:px-6">
        <SectionHeading title="热门视频" subtitle="依据访问量统计的热门视频排行" accent />
        {loading ? <LoadingGrid count={6} columns="grid-cols-1 md:grid-cols-2" /> : <PopularList videos={popular} />}
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 pt-12 md:px-6">
        <SiteIntro />
      </section>
    </>
  );
}

function VideoGrid({ videos: list }: { videos: VideoItem[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
      {list.map((video) => (
        <VideoCard key={video.id} video={video} />
      ))}
    </div>
  );
}